import { useState, type FormEvent, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { AuthShell } from "../components/AuthShell";
import { PasswordField } from "../components/PasswordField";

// Set a new password from the emailed token (?token=). Mirrors the legacy
// reset_password page: strength meter + confirm-match + token-error state.
export function ResetPassword(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api<{ detail: string }>("POST", "/auth/password/reset/confirm", {
        json: { token, new_password: password },
        auth: false,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset your password.");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="This reset link is invalid.">
        <p className="form-error">Missing or invalid reset token.</p>
        <p className="auth-alt">
          <Link to="/forgot-password">Request a new reset link</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Choose a new password for your account.">
      {done ? (
        <>
          <p className="form-ok">Your password has been reset. You can sign in now.</p>
          <p className="auth-alt">
            <Link to="/login">Go to sign in</Link>
          </p>
        </>
      ) : (
        <form onSubmit={onSubmit} className="auth-form">
          <PasswordField label="New password" value={password} onChange={setPassword} />
          <label className="field">
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <button type="submit" className="btn-primary-glow btn-block" disabled={busy}>
            {busy ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
